import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../config/axios";
import { errorHandler } from "../../../helpers/handlers";
import {
  signInWithEmailAndPassword,
  auth,
  signOut,
  clearTokens,
  authTokenKey,
} from "../../../config/firebase";

const initialState = {
  user: null,
  rememberMe: false,
  error: null,
  role: null,
  loading: false, // Track loading state here
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true; // Set loading to true when the login request starts
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false; // Set loading to false when login is successful
        state.user = action.payload.user; // Save user data
        state.rememberMe = action.payload.rememberMe;
        state.role = action.payload.role;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false; // Set loading to false when login fails
        state.error = action.payload; // Store the error
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, () => initialState)
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(authenticateUserWithAccessToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(authenticateUserWithAccessToken.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.role = action.payload.role;
        state.error = null;
      })
      .addCase(authenticateUserWithAccessToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await signInWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );

      if (credentials.rememberMe) {
        localStorage.setItem(authTokenKey, true);
      } else {
        sessionStorage.setItem(authTokenKey, true);
      }

      console.log("Access token", response.user.accessToken);
      const loginResponse = await api.post("/admin/auth/login", {
        email: credentials.email,
        password: credentials.password,
      });
      const user = loginResponse.data.payload;
      user.role = user.userType; // it is stored in the user table as userType

      if (response.user.email === "admin@purplebook.ng") {
        user.role = user.role || "admin";
      }

      return {
        user,
        rememberMe: credentials.rememberMe,
      };
    } catch (error) {
      clearTokens();
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await signOut(auth);
      clearTokens();
      return;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const authenticateUserWithAccessToken = createAsyncThunk(
  "auth/user",
  async (_, { rejectWithValue }) => {
    try {
      console.log("Authenticating user");
      const response = await api.get("/user/auth");
      const user = response.data.payload;
      user.role = user.userType; // it is stored in the user table as userType

      if (user.email === "admin@purplebook.ng") {
        user.role = user.role || "admin";
      }

      return {
        user,
        role: user.role,
      };
    } catch (error) {
      clearTokens();
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export default authSlice.reducer;
